import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi, useWorkspace } from '~/contexts';
import MultiSelect from '../MultiSelect';

export default function ProjectTaskFilter({ placeholder = 'Project Task', name, value, onChange, project }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    setData(null);
    setOptions([]);
  }, [project]);

  const handleSearch = useCallback(
    async (q) => {
      let results = data;
      if (!data) {
        const res = await api.www.workspaces(workspace.id).projects(project.id).tasks().options();
        results = res.data;
        setData(results);
      }
      if (q) {
        results = new Fuse(results, {
          keys: ['name'],
          threshold: 0.4,
        })
          .search(q)
          .map((r) => r.item);
      }
      setOptions(results);
    },
    [workspace.id, api, data, project],
  );

  const groups = useMemo(() => {
    if (!data) return null;

    const byId = _.keyBy(data, 'id');
    const byParent = _.groupBy(data, 'parentId');

    const collectAncestors = (task) => {
      const chain = [];
      let current = task;
      const seen = new Set();
      while (current && !seen.has(current.id)) {
        seen.add(current.id);
        chain.unshift(current);
        current = byId[current.parentId];
      }
      return chain;
    };

    const buildTooltip = (task) => {
      const names = [];
      let current = task;
      const seen = new Set();
      while (current && !seen.has(current.id)) {
        names.unshift(current.name);
        seen.add(current.id);
        current = byId[current.parentId];
      }
      return names.join(' → ');
    };

    const hierarchyMap = {
      Selected: [],
      Active: [],
      Archived: [],
    };

    const seen = {
      Selected: new Set(),
      Active: new Set(),
      Archived: new Set(),
    };

    const insertTree = (parentId, group, depth) => {
      const children = _.sortBy(byParent[parentId] || [], 'position');
      for (const task of children) {
        if (!seen[group].has(task.id)) continue;

        hierarchyMap[group].push({
          task,
          depth,
          tooltip: buildTooltip(task),
        });

        seen[group].delete(task.id); // mark as inserted
        insertTree(task.id, group, depth + 1);
      }
    };

    for (const task of options) {
      const isSelected = value?.some((v) => v.id === task.id);
      const group = isSelected ? 'Selected' : task.recordStatusId === 'active' ? 'Active' : 'Archived';

      if (group === 'Selected') {
        if (!seen.Selected.has(task.id)) {
          hierarchyMap.Selected.push({
            task,
            depth: 0,
            tooltip: buildTooltip(task),
          });
          seen.Selected.add(task.id); // still mark as seen to prevent dupes
        }
      } else {
        const chain = collectAncestors(task);
        chain.forEach((t) => seen[group].add(t.id));
      }
    }

    insertTree(null, 'Active', 0);
    insertTree(null, 'Archived', 0);

    const allOptions = _.uniqBy(
      [..._.flatMap(Object.values(hierarchyMap)).map(({ task }) => task), ...(value || [])],
      'id',
    );

    return { groups: hierarchyMap, allOptions };
  }, [data, options, value]);

  return (
    <MultiSelect.Filter
      icon="filter"
      placeholder={placeholder}
      name={name}
      value={value}
      renderValue={(value) => value.map((v) => v.name).join('; ')}
      options={groups?.allOptions || []}
      renderOptions={(opts) => {
        const optsById = _.keyBy(opts, 'id');

        return Object.entries(groups?.groups || {})
          .filter(([, tasks]) => tasks.length > 0)
          .map(([status, tasks]) => (
            <MultiSelect.Group key={status} label={status}>
              {tasks.map(({ task, depth, tooltip }) => (
                <MultiSelect.Option
                  key={task.id}
                  value={optsById[task.id] || task}
                  inactive={task.recordStatusId !== 'active'}
                  tooltip={tooltip}
                  style={{ paddingLeft: `${1 + (depth || 0) * 1}rem` }}
                  {...(optsById[task.id]?.props || {})}>
                  {task.name}
                </MultiSelect.Option>
              ))}
            </MultiSelect.Group>
          ));
      }}
      isLoading={!data}
      onSearch={handleSearch}
      onChange={onChange}
    />
  );
}
